import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  MenuItem,
  Button as MuiButton,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { rtlCache } from "../../styles/rtlCache";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { FormikProps, getIn, useFormik } from "formik";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { Button } from "../common/button";
import {
  Client,
  DocumentConfigs,
  GrvMatchingConfig,
  InvoiceItemsConfig,
  WhatsappNumber,
} from "../../types/Client";
import { EmailRecipientsField } from "./EmailRecipientsField";
import { PriorityCompaniesField } from "./PriorityCompaniesField";
import { CopySettingsDialog } from "./CopySettingsDialog";

interface ClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (customerNameOrId: string, client: Partial<Client>) => void;
  customerId: string | null;
  client: Client | null;
}

// Invoice line settings, per form (YINVOICES.items / PINVOICES.items) of each Priority
// company. A consolidated invoice - or a tax invoice posted as one - sends them only when
// it isn't linked to GRVs: a linked one takes its lines from the GRVs in Priority.
const DEFAULT_ITEMS_CONFIG: InvoiceItemsConfig = {
  send: false,
  general_partname: "000",
};

const toItemsConfig = (items?: Partial<InvoiceItemsConfig>): InvoiceItemsConfig => ({
  ...DEFAULT_ITEMS_CONFIG,
  ...items,
});

// Same defaults as the backend (erp_settings.DEFAULT_GRV_MATCHING_CONFIG)
const DEFAULT_GRV_MATCHING_CONFIG: GrvMatchingConfig = {
  enabled: false,
  tolerance: 1.0,
};

const toGrvMatchingConfig = (grv?: Partial<GrvMatchingConfig>): GrvMatchingConfig => ({
  ...DEFAULT_GRV_MATCHING_CONFIG,
  ...grv,
});

// Tolerance isn't editable here (kept in the DB, default ₪1.0), so only the switch is validated
const grvMatchingSchema = yup.object({
  enabled: yup.boolean(),
});

// One Priority company's editable document settings (ERP.priority_companies_configs.<name>)
interface CompanyDocumentSettings {
  name: string;
  YINVOICES: { post_as_pinvoice: boolean; items: InvoiceItemsConfig; grv_matching: GrvMatchingConfig };
  PINVOICES: { items: InvoiceItemsConfig; grv_matching: GrvMatchingConfig };
}

const toDocumentSettings = (name: string, configs?: DocumentConfigs): CompanyDocumentSettings => ({
  name,
  YINVOICES: {
    post_as_pinvoice: configs?.YINVOICES?.post_as_pinvoice ?? false,
    items: toItemsConfig(configs?.YINVOICES?.items),
    grv_matching: toGrvMatchingConfig(configs?.YINVOICES?.grv_matching),
  },
  PINVOICES: {
    items: toItemsConfig(configs?.PINVOICES?.items),
    grv_matching: toGrvMatchingConfig(configs?.PINVOICES?.grv_matching),
  },
});

// The customer's Priority companies; a backend not yet updated returns them only as clients_names
const companiesOf = (client: Client | null): string[] =>
  client?.ERP.priority_companies ?? client?.ERP.clients_names ?? [];

// A company without settings of its own starts from the customer-level ones - what its
// uploads used until now (the backend falls back to them the same way)
const companySettingsFor = (client: Client | null, name: string): CompanyDocumentSettings =>
  toDocumentSettings(name, client?.ERP.priority_companies_configs?.[name] ?? client?.ERP);

// "Copy to": one company's settings onto another - only what the dialog shows; the
// target keeps its hidden values (GRV tolerance)
const copySettings = (from: CompanyDocumentSettings, to: CompanyDocumentSettings): CompanyDocumentSettings => ({
  name: to.name,
  YINVOICES: {
    post_as_pinvoice: from.YINVOICES.post_as_pinvoice,
    items: { ...from.YINVOICES.items },
    grv_matching: { ...to.YINVOICES.grv_matching, enabled: from.YINVOICES.grv_matching.enabled },
  },
  PINVOICES: {
    items: { ...from.PINVOICES.items },
    grv_matching: { ...to.PINVOICES.grv_matching, enabled: from.PINVOICES.grv_matching.enabled },
  },
});

// Only the editable keys; the backend keeps the rest (e.g. allow_upload)
const toDocumentConfigsPayload =(settings: CompanyDocumentSettings): DocumentConfigs => ({
  YINVOICES: {
    post_as_pinvoice: settings.YINVOICES.post_as_pinvoice,
    items: {
      ...settings.YINVOICES.items,
      general_partname: settings.YINVOICES.items.general_partname.trim(),
    },
    grv_matching: {
      // Only applies to invoices posted as consolidated invoices. No tolerance:
      // the backend keeps the stored one
      enabled: settings.YINVOICES.post_as_pinvoice && settings.YINVOICES.grv_matching.enabled,
    },
  },
  PINVOICES: {
    items: {
      ...settings.PINVOICES.items,
      general_partname: settings.PINVOICES.items.general_partname.trim(),
    },
    grv_matching: {
      enabled: settings.PINVOICES.grv_matching.enabled,
    },
  },
});

// GRV linking in effect for a form: PINVOICES' own switch, or YINVOICES' while its
// invoices are posted as consolidated invoices
const grvLinkingOn = (settings: CompanyDocumentSettings, form: "YINVOICES" | "PINVOICES"): boolean =>
  form === "PINVOICES"
    ? settings.PINVOICES.grv_matching.enabled
    : settings.YINVOICES.post_as_pinvoice && settings.YINVOICES.grv_matching.enabled;

const itemsSchema = yup.object({
  send: yup.boolean(),
  // Every sent line carries the general part number (same rule the backend applies)
  general_partname: yup.string().when("send", {
    is: true,
    then: (schema) => schema.trim().required("General part number is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const validationSchema = yup.object({
  customerName: yup.string().required("Customer name is required"),
  email: yup
    .string()
    .email("Must be a valid email")
    .required("Email is required"),
  email_password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .required("Email password is required"),
  base_url: yup
    .string()
    .url("Must be a valid URL")
    .required("Base URL is required"),
  auth_type: yup
    .string()
    .oneOf(["priority_token", "username_password"], "Invalid auth type")
    .required("Auth type is required"),
  priority_token: yup.string().when("auth_type", {
    is: "priority_token",
    then: (schema) => schema.required("Priority token is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  username: yup.string().when("auth_type", {
    is: "username_password",
    then: (schema) => schema.required("Username is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  password: yup.string().when("auth_type", {
    is: "username_password",
    then: (schema) => schema.required("Password is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  company_configs: yup.array().of(
    yup.object({
      YINVOICES: yup.object({ items: itemsSchema, grv_matching: grvMatchingSchema }),
      PINVOICES: yup.object({ items: itemsSchema, grv_matching: grvMatchingSchema }),
    })
  ),
  monthly_quota: yup
    .number()
    .positive("Must be a positive number")
    .integer("Must be an integer"),
});

// The dialog's accordions (email, optional settings)
const accordionSx = {
  border: "1px solid #e3f2fd",
  borderRadius: "8px !important",
  boxShadow: "0 2px 4px rgba(33, 150, 243, 0.1)",
  "&:before": { display: "none" },
  backgroundColor: "#f8fcff",
};

const accordionSummarySx = {
  backgroundColor: "#e3f2fd",
  borderRadius: "8px",
  "&.Mui-expanded": {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
};

// Older customer docs store a single {number} object instead of a list
const toWhatsappList = (whatsapp: unknown): WhatsappNumber[] => {
  const entries = Array.isArray(whatsapp) ? whatsapp : whatsapp ? [whatsapp] : [];
  return entries
    .filter((entry: any) => entry && typeof entry.number === "string")
    .map((entry: any) => ({
      number: entry.number,
      priority_client: entry.priority_client || "",
    }));
};

// Props of a company's document fields: company_configs[index], one invoice form
interface CompanyFormFieldsProps {
  formik: FormikProps<any>;
  index: number;
  form: "YINVOICES" | "PINVOICES";
}

// "Send invoice lines" switch + general part number, for either invoice form. The
// part number is always shown (always saved, default "000"), required while lines are sent.
const InvoiceItemsFields: React.FC<CompanyFormFieldsProps> = ({ formik, index, form }) => {
  const { t } = useTranslation();
  const settings: CompanyDocumentSettings = formik.values.company_configs[index];
  const items: InvoiceItemsConfig = settings[form].items;
  const field = (name: keyof InvoiceItemsConfig) => `company_configs.${index}.${form}.items.${name}`;
  const partnameError = getIn(formik.errors, field("general_partname"));
  const showPartnameError =
    Boolean(partnameError) && Boolean(getIn(formik.touched, field("general_partname")));

  return (
    <>
      <FormControlLabel
        control={
          <Switch
            checked={items.send}
            onChange={(e) => formik.setFieldValue(field("send"), e.target.checked)}
          />
        }
        label={t("SEND_ITEMS") || "Send invoice lines to Priority"}
      />
      {items.send && grvLinkingOn(settings, form) && (
        <Typography variant="body2" color="text.secondary">
          {t("ITEMS_WHEN_NOT_LINKED") ||
            "Lines are sent only when the invoice isn't linked to goods receipts; a linked invoice takes its lines from them."}
        </Typography>
      )}

      <TextField
        fullWidth
        required={items.send}
        label={t("GENERAL_PARTNAME") || "General Part Number"}
        name={field("general_partname")}
        value={items.general_partname}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={showPartnameError}
        helperText={
          (showPartnameError && partnameError) ||
          t("GENERAL_PARTNAME_HINT") ||
          "Used for every invoice line"
        }
        inputProps={{ dir: "ltr" }}
      />
    </>
  );
};

// "Link to goods receipts" switch. YINVOICES and PINVOICES each have their own
// grv_matching; the YINVOICES one applies to invoices posted as PINVOICES. The
// tolerance isn't shown (customers wouldn't know what to set): it stays in the DB,
// default ₪1.0, and is left out of the save payload so the stored value is kept.
const GrvMatchingFields: React.FC<CompanyFormFieldsProps> = ({ formik, index, form }) => {
  const { t } = useTranslation();
  const grv: GrvMatchingConfig = formik.values.company_configs[index][form].grv_matching;

  return (
    <FormControlLabel
      control={
        <Switch
          checked={grv.enabled}
          onChange={(e) =>
            formik.setFieldValue(`company_configs.${index}.${form}.grv_matching.enabled`, e.target.checked)
          }
        />
      }
      label={t("GRV_MATCHING") || "Link to goods receipts"}
    />
  );
};

const ClientDialog: React.FC<ClientDialogProps> = ({
  open,
  onClose,
  onSave,
  customerId,
  client,
}) => {
  const { t } = useTranslation();
  const isEditing = !!customerId;
  // The company whose settings the "copy to" dialog copies, while it's open
  const [copySource, setCopySource] = useState<string | null>(null);
  // The company tab shown (by name, so adding/removing companies keeps it); the first by default
  const [activeCompany, setActiveCompany] = useState<string | null>(null);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    // A WhatsApp number's company must be one of priority_companies, whose document
    // settings its uploads use (the backend rejects anything else). Merged with the schema errors
    validate: (values) => {
      const whatsapp = values.whatsapp.map((entry) =>
        entry.priority_client && !values.priority_companies.includes(entry.priority_client)
          ? { priority_client: "Not one of the Priority companies" }
          : {}
      );
      return whatsapp.some((entry) => "priority_client" in entry) ? { whatsapp } : {};
    },
    initialValues: {
      customerName: client?.customer_name || "",
      email: client?.email || "",
      email_password: client?.email_password || "",
      base_url: client?.ERP.base_url || "",
      priority_companies: companiesOf(client),
      auth_type: (client?.ERP.auth_type || "priority_token") as "priority_token" | "username_password",
      priority_token: client?.ERP.priority_token || "",
      username: client?.ERP.username || "",
      password: client?.ERP.password || "",
      // One entry per priority_companies entry, in the same order (kept in sync on edit)
      company_configs: companiesOf(client).map((name) => companySettingsFor(client, name)),
      whatsapp: toWhatsappList(client?.whatsapp),
      success_mails: client?.configuration?.success_mails || [],
      failure_mails: client?.configuration?.failure_mails || [],
      daily_digest_recipients:
        client?.configuration?.daily_digest_recipients || [],
      quota_warning: client?.configuration?.quota_warning || [],
      monthly_quota: client?.subscription?.monthly_quota || 300,
      auto_renew: client?.subscription?.auto_renew ?? true,
    },
    onSubmit: (values) => {
      const clientData: Partial<Client> = {
        customer_name: values.customerName,
        email: values.email,
        email_password: values.email_password,
        ERP: {
          enabled: true,
          base_url: values.base_url,
          priority_companies: values.priority_companies,
          auth_type: values.auth_type,
          ...(values.auth_type === "priority_token"
            ? { priority_token: values.priority_token }
            : { username: values.username, password: values.password }
          ),
          // Per company. The customer-level YINVOICES/PINVOICES aren't sent: the
          // backend keeps them as the fallback for a company without settings
          priority_companies_configs: Object.fromEntries(
            values.company_configs.map((settings) => [settings.name, toDocumentConfigsPayload(settings)])
          ),
        },
        configuration: {
          success_mails: values.success_mails,
          failure_mails: values.failure_mails,
          daily_digest_recipients: values.daily_digest_recipients,
          quota_warning: values.quota_warning,
        },
      };

      // Only existing numbers' priority_client is sent; the server ignores unknown numbers
      if (isEditing && values.whatsapp.length > 0) {
        clientData.whatsapp = values.whatsapp;
      }

      // Add optional fields for new customers
      if (!isEditing) {
        (clientData as any).token_name = values.customerName;
        (clientData as any).folder_name = values.customerName;
        (clientData as any).monthly_quota = values.monthly_quota;
        (clientData as any).auto_renew = values.auto_renew;
      }

      // Use customerId for updates, customerName for new customers
      onSave(isEditing ? customerId! : values.customerName, clientData);
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setActiveCompany(null);
    }
  }, [open]);

  const companyConfigs = formik.values.company_configs;
  const activeIndex = Math.max(0, companyConfigs.findIndex((settings) => settings.name === activeCompany));
  const activeSettings: CompanyDocumentSettings | undefined = companyConfigs[activeIndex];
  // Errors show regardless of touched: a tab not on screen can be what keeps Update disabled
  const companyHasError = (index: number) => Boolean(getIn(formik.errors, `company_configs.${index}`));
  // WhatsApp numbers whose uploads go to a company - from the form, so a re-picked number shows at once
  const whatsappNumbersOf = (name: string) =>
    formik.values.whatsapp.filter((entry) => entry.priority_client === name).map((entry) => entry.number);
  const activeWhatsappNumbers = activeSettings ? whatsappNumbersOf(activeSettings.name) : [];

  // In the form only - saved with the rest on Update
  const copyCompanySettings = (source: string, targets: string[]) => {
    const from = formik.values.company_configs.find((settings) => settings.name === source);
    if (from) {
      formik.setFieldValue(
        "company_configs",
        formik.values.company_configs.map((settings) =>
          targets.includes(settings.name) ? copySettings(from, settings) : settings
        )
      );
    }
    setCopySource(null);
  };

  return (
    // Hebrew/Arabic: right-to-left, with MUI's styles flipped (styles/rtlCache.ts)
    <CacheProvider value={rtlCache}>
      <Dialog dir="rtl" open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? `${t("EDIT_CUSTOMER")}: ${client?.customer_name}` : t("ADD_CUSTOMER")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label={t("CUSTOMER_NAME")}
              name="customerName"
              value={formik.values.customerName}
              onChange={formik.handleChange}
              error={formik.touched.customerName && Boolean(formik.errors.customerName)}
              helperText={
                (formik.touched.customerName && formik.errors.customerName) ||
                "Enter a unique customer name (e.g., yael_demo)"
              }
              disabled={isEditing}
            />

            <TextField
              fullWidth
              label={t("EMAIL") || "Client Email"}
              name="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={
                (formik.touched.email && formik.errors.email) ||
                "Enter a valid email address (e.g., user@example.com)"
              }
              required
            />

            <TextField
              fullWidth
              label={t("EMAIL_PASSWORD") || "Email Password"}
              name="email_password"
              type="password"
              value={formik.values.email_password}
              onChange={formik.handleChange}
              error={
                formik.touched.email_password &&
                Boolean(formik.errors.email_password)
              }
              helperText={
                (formik.touched.email_password && formik.errors.email_password) ||
                (isEditing
                  ? "Leave blank to keep current password"
                  : "Minimum 8 characters required")
              }
              required={!isEditing}
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              {t("ERP_CONFIGURATION")}
            </Typography>

            <TextField
              fullWidth
              label={t("BASE_URL")}
              name="base_url"
              value={formik.values.base_url}
              onChange={formik.handleChange}
              error={formik.touched.base_url && Boolean(formik.errors.base_url)}
              helperText={
                (formik.touched.base_url && formik.errors.base_url) ||
                "Enter a valid URL (e.g., https://api.example.com)"
              }
            />

            <PriorityCompaniesField
              label={t("PRIORITY_COMPANIES") || "Priority Companies"}
              companies={formik.values.priority_companies}
              onChange={(companies) => {
                // A company just added (appended last) opens in its tab
                if (companies.length > formik.values.priority_companies.length) {
                  setActiveCompany(companies[companies.length - 1]);
                }
                formik.setFieldValue("priority_companies", companies);
                // Keep one settings entry per company: edited ones stay, new ones start
                // from their stored settings or the customer-level ones
                formik.setFieldValue(
                  "company_configs",
                  companies.map(
                    (name) =>
                      formik.values.company_configs.find((settings) => settings.name === name) ??
                      companySettingsFor(client, name)
                  )
                );
              }}
            />

            {/* WhatsApp numbers and their company, compact under the companies they pick from */}
            {isEditing && formik.values.whatsapp.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <WhatsAppIcon fontSize="small" color="success" />
                  {t("WHATSAPP_NUMBERS") || "WhatsApp Numbers"}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                  {formik.values.whatsapp.map((entry, index) => {
                    // A company set before it had to be one of priority_companies: shown, but must be re-picked
                    const notInList =
                      Boolean(entry.priority_client) &&
                      !formik.values.priority_companies.includes(entry.priority_client!);
                    return (
                      <Box key={entry.number} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                        <TextField
                          size="small"
                          label={t("WHATSAPP_NUMBER") || "WhatsApp Number"}
                          value={entry.number}
                          disabled
                          inputProps={{ dir: "ltr" }}
                          sx={{ width: 200, backgroundColor: "#fff" }}
                        />
                        <TextField
                          select
                          size="small"
                          label={t("PRIORITY_COMPANY") || "Priority Company"}
                          name={`whatsapp[${index}].priority_client`}
                          value={entry.priority_client || ""}
                          onChange={formik.handleChange}
                          error={notInList}
                          helperText={
                            notInList
                              ? t("PRIORITY_COMPANY_NOT_IN_LIST") ||
                                "Not one of the Priority companies - pick a company from the list"
                              : entry.priority_client
                                ? undefined
                                : t("PRIORITY_COMPANY_REQUIRED_HINT") ||
                                  "Uploads from this number are rejected until a company is set"
                          }
                          sx={{ width: 200, "& .MuiInputBase-root": { backgroundColor: "#fff" } }}
                        >
                          <MenuItem value="">{t("NO_PRIORITY_COMPANY") || "None"}</MenuItem>
                          {formik.values.priority_companies.map((name) => (
                            <MenuItem key={name} value={name} dir="ltr">
                              {name}
                            </MenuItem>
                          ))}
                          {notInList && (
                            <MenuItem value={entry.priority_client!} disabled dir="ltr">
                              {entry.priority_client}
                            </MenuItem>
                          )}
                        </TextField>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">
                {t("AUTH_METHOD") || "Authentication Method"}
              </FormLabel>
              <RadioGroup
                row
                name="auth_type"
                value={formik.values.auth_type}
                onChange={formik.handleChange}
              >
                <FormControlLabel
                  value="priority_token"
                  control={<Radio />}
                  label={t("PRIORITY_TOKEN") || "Priority Token"}
                />
                <FormControlLabel
                  value="username_password"
                  control={<Radio />}
                  label={t("USERNAME_PASSWORD") || "Username & Password"}
                />
              </RadioGroup>
            </FormControl>

            {formik.values.auth_type === "priority_token" && (
              <TextField
                fullWidth
                label={t("PRIORITY_TOKEN")}
                name="priority_token"
                value={formik.values.priority_token}
                onChange={formik.handleChange}
                error={
                  formik.touched.priority_token && Boolean(formik.errors.priority_token)
                }
                helperText={
                  (formik.touched.priority_token && formik.errors.priority_token) ||
                  "Enter the priority token for the API"
                }
              />
            )}

            {formik.values.auth_type === "username_password" && (
              <>
                <TextField
                  fullWidth
                  label={t("USERNAME") || "Username"}
                  name="username"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.username && Boolean(formik.errors.username)
                  }
                  helperText={
                    (formik.touched.username && formik.errors.username) ||
                    "Enter the API username"
                  }
                />
                <TextField
                  fullWidth
                  label={t("PASSWORD") || "Password"}
                  name="password"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={
                    (formik.touched.password && formik.errors.password) ||
                    "Enter the API password (will be encoded as Base64)"
                  }
                />
              </>
            )}

            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
              {t("PRIORITY_DOCUMENT_SETTINGS") || "Priority Document Settings"}
            </Typography>

            {formik.values.company_configs.length === 0 && (
              <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                {t("NO_COMPANIES_FOR_DOCUMENT_SETTINGS") ||
                  "Add a Priority company above to set its document settings"}
              </Typography>
            )}

            {/* One tab per Priority company (priority_companies): its uploads use these settings */}
            {activeSettings && (
              <Box sx={{ border: "1px solid #e3f2fd", borderRadius: 2, boxShadow: "0 2px 4px rgba(33, 150, 243, 0.1)" }}>
                <Tabs
                  value={activeIndex}
                  onChange={(_, index: number) => setActiveCompany(companyConfigs[index].name)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ backgroundColor: "#e3f2fd", borderRadius: "8px 8px 0 0" }}
                >
                  {companyConfigs.map((settings, index) => {
                    // Green: WhatsApp uploads go to this company
                    const viaWhatsapp = whatsappNumbersOf(settings.name).length > 0;
                    return (
                      <Tab
                        key={settings.name}
                        value={index}
                        label={
                          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            {viaWhatsapp && <WhatsAppIcon fontSize="small" />}
                            <span dir="ltr">{settings.name}</span>
                          </Box>
                        }
                        icon={companyHasError(index) ? <ErrorOutlineIcon color="error" fontSize="small" /> : undefined}
                        iconPosition="end"
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          minHeight: 48,
                          ...(viaWhatsapp && {
                            color: "success.main",
                            backgroundColor: "#e8f5e9",
                            "&.Mui-selected": { color: "success.dark" },
                          }),
                        }}
                      />
                    );
                  })}
                </Tabs>

                <Box sx={{ backgroundColor: "#f8fcff", p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  {(activeWhatsappNumbers.length > 0 || companyConfigs.length > 1) && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      {activeWhatsappNumbers.length > 0 ? (
                        <Chip
                          icon={<WhatsAppIcon />}
                          color="success"
                          variant="outlined"
                          label={
                            <>
                              {t("USED_BY_WHATSAPP") || "WhatsApp uploads go to this company"}:{" "}
                              <span dir="ltr">{activeWhatsappNumbers.join(", ")}</span>
                            </>
                          }
                        />
                      ) : (
                        <span />
                      )}
                      {companyConfigs.length > 1 && (
                        <MuiButton
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => setCopySource(activeSettings.name)}
                          sx={{ textTransform: "none" }}
                        >
                          {t("COPY_SETTINGS_TO") || "Copy settings to..."}
                        </MuiButton>
                      )}
                    </Box>
                  )}
                  <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1, backgroundColor: "#fff" }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {t("YINVOICE_SETTINGS") || "Tax Invoice (YINVOICES)"}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={activeSettings.YINVOICES.post_as_pinvoice}
                          onChange={(e) =>
                            formik.setFieldValue(`company_configs.${activeIndex}.YINVOICES.post_as_pinvoice`, e.target.checked)
                          }
                        />
                      }
                      label={
                        t("FORCE_YINVOICE_AS_PINVOICE") ||
                        "Post tax invoices as consolidated invoices (PINVOICES)"
                      }
                    />
                    {activeSettings.YINVOICES.post_as_pinvoice && (
                      <GrvMatchingFields formik={formik} index={activeIndex} form="YINVOICES" />
                    )}
                    <InvoiceItemsFields formik={formik} index={activeIndex} form="YINVOICES" />
                  </Box>

                  <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1, backgroundColor: "#fff" }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {t("PINVOICE_SETTINGS") || "Consolidated Invoice (PINVOICES)"}
                    </Typography>
                    <GrvMatchingFields formik={formik} index={activeIndex} form="PINVOICES" />
                    <InvoiceItemsFields formik={formik} index={activeIndex} form="PINVOICES" />
                  </Box>
                </Box>
              </Box>
            )}

            <Accordion sx={{ ...accordionSx, mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                <Typography sx={{ fontWeight: 600, color: "#1976d2" }}>
                  {t("EMAIL_CONFIGURATION") || "Email Configuration"}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ backgroundColor: "#f8fcff", pt: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <EmailRecipientsField
                    label={t("SUCCESS_EMAILS") || "Success Email Recipients"}
                    secondaryLabels={t("DISPLAY_NAME") || "Display Name"}
                    recipients={formik.values.success_mails}
                    onChange={(recipients) =>
                      formik.setFieldValue("success_mails", recipients)
                    }
                  />
                  <EmailRecipientsField
                    label={t("FAILURE_EMAILS") || "Failure Email Recipients"}
                    secondaryLabels={t("DISPLAY_NAME") || "Display Name"}
                    recipients={formik.values.failure_mails}
                    onChange={(recipients) =>
                      formik.setFieldValue("failure_mails", recipients)
                    }
                  />
                  <EmailRecipientsField
                    label={t("DAILY_DIGEST_EMAILS") || "Daily Digest Recipients"}
                    secondaryLabels={t("DISPLAY_NAME") || "Display Name"}
                    recipients={formik.values.daily_digest_recipients}
                    onChange={(recipients) =>
                      formik.setFieldValue("daily_digest_recipients", recipients)
                    }
                  />
                  <EmailRecipientsField
                    label={t("QUOTA_WARNING_EMAILS") || "Quota Warning Recipients"}
                    secondaryLabels={t("DISPLAY_NAME") || "Display Name"}
                    recipients={formik.values.quota_warning}
                    onChange={(recipients) =>
                      formik.setFieldValue("quota_warning", recipients)
                    }
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={{ ...accordionSx, mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                <Typography sx={{ fontWeight: 600, color: "#1976d2" }}>
                  {t("OPTIONAL_SETTINGS") || "Optional Settings"}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ backgroundColor: "#f8fcff", pt: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t("MONTHLY_QUOTA") || "Monthly Quota"}
                    name="monthly_quota"
                    type="number"
                    value={formik.values.monthly_quota}
                    onChange={formik.handleChange}
                    // Sent only when the customer is created: PUT /customer/<id> doesn't take it
                    disabled={isEditing}
                    error={formik.touched.monthly_quota && Boolean(formik.errors.monthly_quota)}
                    helperText={
                      (formik.touched.monthly_quota && formik.errors.monthly_quota) ||
                      (isEditing
                        ? t("QUOTA_SET_ON_CREATE") || "Set when the customer is created - can't be changed here"
                        : "Enter a positive integer (Default: 300)")
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.auto_renew}
                        onChange={(e) => formik.setFieldValue("auto_renew", e.target.checked)}
                        disabled={isEditing}
                      />
                    }
                    label={t("AUTO_RENEW") || "Auto-renew subscription"}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <CopySettingsDialog
          open={copySource !== null}
          source={copySource ?? ""}
          companies={formik.values.priority_companies}
          onClose={() => setCopySource(null)}
          onCopy={(targets) => copyCompanySettings(copySource!, targets)}
        />
        <DialogActions>
          <Button label={t("CANCEL")} onClick={onClose} />
          <Button
            label={isEditing ? t("UPDATE") : t("ADD")}
            onClick={formik.submitForm}
            disabled={!formik.isValid || !formik.dirty}
          />
        </DialogActions>
      </Dialog>
    </CacheProvider>
  );
};

export default ClientDialog;
